import { hashKey, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { CurrentUser } from "@/features/auth/currentUserTypes";
import {
  fetchOrderOverview,
  fetchDashboardV2Analytics,
  fetchDashboardV2Detail,
  fetchDashboardV2Operations,
  fetchDashboardV2Ranking,
  DashboardStoreScopeMismatchError,
  fetchSupplierCurrentLoads,
} from "./api";
import type { DashboardDetailKind } from "./types";
import {
  DEFAULT_DASHBOARD_PERIOD_STATE,
  type DashboardPeriodState,
} from "./period";
import {
  dashboardKeys,
  dashboardAuthorityFingerprint,
  type DashboardAuthorityKeyInput,
  type DashboardDetailKeyInput,
  type DashboardRankingContext,
  type DashboardRankingFetchInput,
  type DashboardRankingKeyInput,
} from "./queryKeys";

export class DashboardAuthorityContractError extends TypeError {
  constructor(message: string) {
    super(`invalid dashboard authority: ${message}`);
    this.name = "DashboardAuthorityContractError";
  }
}

const requiredAuthorityId = (value: unknown, field: string): string => {
  if (typeof value !== "string" || value.length === 0) {
    throw new DashboardAuthorityContractError(`${field} is required`);
  }
  return value;
};

export function dashboardAuthorityFromCurrentUser(
  user: CurrentUser,
  selectedStoreId?: string,
): DashboardAuthorityKeyInput {
  const actorId = requiredAuthorityId(user.id, "id");
  switch (user.role) {
    case "super":
    case "admin_helper":
    case "checker":
    case "overviewer":
      return { role: user.role, actorId };
    case "client":
      return {
        role: user.role,
        actorId,
        clientId: requiredAuthorityId(user.client_id, "client_id"),
      };
    case "supplier":
      return {
        role: user.role,
        actorId,
        supplierId: requiredAuthorityId(user.supplier_id, "supplier_id"),
      };
    case "client_store": {
      const clientId = requiredAuthorityId(user.client_id, "client_id");
      const access = user.store_access;
      if (
        access === null ||
        access === undefined ||
        (access.scope_type !== "all" && access.scope_type !== "selected") ||
        !Array.isArray(access.store_ids) ||
        (access.scope_type === "all" && access.store_ids.length !== 0) ||
        access.store_ids.some(
          (storeId) => typeof storeId !== "string" || storeId.length === 0,
        )
      ) {
        throw new DashboardAuthorityContractError(
          "store_access with scope_type and store_ids is required",
        );
      }
      if (
        selectedStoreId &&
        access.scope_type === "selected" &&
        !access.store_ids.includes(selectedStoreId)
      ) {
        throw new DashboardAuthorityContractError(
          "selected store is outside store_access",
        );
      }
      return {
        role: user.role,
        actorId,
        clientId,
        storeScope: {
          scopeType: access.scope_type,
          storeIds: access.store_ids,
        },
        ...(selectedStoreId ? { selectedStoreId } : {}),
      };
    }
    default:
      throw new DashboardAuthorityContractError("role is invalid");
  }
}

interface DashboardAuthorityState {
  authority: DashboardAuthorityKeyInput | null;
  error: DashboardAuthorityContractError | null;
}

const resolveDashboardAuthority = (
  user: CurrentUser | null | undefined,
  selectedStoreId?: string,
): DashboardAuthorityState => {
  if (!user) return { authority: null, error: null };
  try {
    return {
      authority: dashboardAuthorityFromCurrentUser(user, selectedStoreId),
      error: null,
    };
  } catch (error) {
    return {
      authority: null,
      error:
        error instanceof DashboardAuthorityContractError
          ? error
          : new DashboardAuthorityContractError("unknown authority contract"),
    };
  }
};

export interface DashboardV2AnalyticsHookInput {
  user?: CurrentUser | null;
  period: DashboardPeriodState;
  enabled?: boolean;
  selectedStoreId?: string;
}

export function useDashboardV2Analytics({
  user,
  period,
  enabled = true,
  selectedStoreId,
}: DashboardV2AnalyticsHookInput) {
  const authorityState = useMemo(
    () => resolveDashboardAuthority(user, selectedStoreId),
    [user, selectedStoreId],
  );
  const { authority } = authorityState;
  const authorityHash = authority
    ? hashKey(dashboardAuthorityFingerprint(authority))
    : null;
  const query = useQuery({
    queryKey: authority
      ? dashboardKeys.analytics({
          ...authority,
          period: period.period,
          from: period.from,
          to: period.to,
          history: period.history,
        })
      : [...dashboardKeys.all, "analytics", "disabled", user?.id ?? null],
    queryFn: ({ signal }) => {
      if (!authority)
        throw new DashboardAuthorityContractError("authority missing");
      return fetchDashboardV2Analytics(authority, period, signal);
    },
    enabled: enabled && authority !== null,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    placeholderData: (previousData, previousQuery) =>
      authorityHash !== null &&
      previousQuery?.meta?.dashboardAnalyticsAuthorityHash === authorityHash
        ? previousData
        : undefined,
    meta: { dashboardAnalyticsAuthorityHash: authorityHash },
    retry: (failures, error) =>
      !(error instanceof DashboardStoreScopeMismatchError) && failures < 1,
  });
  return { ...query, authorityError: authorityState.error };
}

export interface DashboardV2OperationsHookInput {
  user?: CurrentUser | null;
  selectedStoreId?: string;
  enabled?: boolean;
}

export function useDashboardV2Operations({
  user,
  selectedStoreId,
  enabled = true,
}: DashboardV2OperationsHookInput) {
  const authorityState = useMemo(
    () => resolveDashboardAuthority(user, selectedStoreId),
    [user, selectedStoreId],
  );
  const { authority } = authorityState;
  const query = useQuery({
    queryKey: authority
      ? dashboardKeys.operations(authority)
      : [...dashboardKeys.all, "operations", "disabled", user?.id ?? null],
    queryFn: ({ signal }) => {
      if (!authority)
        throw new DashboardAuthorityContractError("authority missing");
      return fetchDashboardV2Operations(authority, signal);
    },
    enabled: enabled && authority !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    retry: (failures, error) =>
      !(error instanceof DashboardStoreScopeMismatchError) && failures < 1,
  });
  return { ...query, authorityError: authorityState.error };
}

export function useSupplierCurrentLoads(user: CurrentUser) {
  const authorityState = useMemo(() => resolveDashboardAuthority(user), [user]);
  const authority =
    authorityState.authority?.role === "supplier"
      ? authorityState.authority
      : null;
  const query = useInfiniteQuery({
    queryKey: authority
      ? dashboardKeys.supplierCurrentLoads(authority)
      : [
          ...dashboardKeys.all,
          "supplier-current-loads",
          "disabled",
          user.id,
          user.role,
        ],
    queryFn: ({ signal, pageParam }) => {
      if (!authority)
        throw new DashboardAuthorityContractError("supplier authority missing");
      return fetchSupplierCurrentLoads(
        authority,
        typeof pageParam === "string" ? pageParam : undefined,
        signal,
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_cursor : undefined,
    enabled: authority !== null,
    staleTime: 15_000,
    retry: 1,
  });
  return { ...query, authorityError: authorityState.error };
}

export type DashboardV2QueriesInput = DashboardV2AnalyticsHookInput;

export function useDashboardV2Queries(input: DashboardV2QueriesInput) {
  const analytics = useDashboardV2Analytics(input);
  const operations = useDashboardV2Operations({ user: input.user });
  return {
    analytics,
    operations,
    authorityError: analytics.authorityError ?? operations.authorityError,
  };
}

export interface DashboardV2PeriodQueriesInput {
  user?: CurrentUser | null;
  activePeriod: DashboardPeriodState | null;
  candidatePeriod: DashboardPeriodState;
  candidateEnabled: boolean;
  selectedStoreId?: string;
}

export function useDashboardV2PeriodQueries({
  user,
  activePeriod,
  candidatePeriod,
  candidateEnabled,
  selectedStoreId,
}: DashboardV2PeriodQueriesInput) {
  const scopeReady = user?.role !== "client_store" || Boolean(selectedStoreId);
  const activeAnalytics = useDashboardV2Analytics({
    user,
    period: activePeriod ?? DEFAULT_DASHBOARD_PERIOD_STATE,
    enabled: activePeriod !== null && scopeReady,
    selectedStoreId,
  });
  const candidateAnalytics = useDashboardV2Analytics({
    user,
    period: candidatePeriod,
    enabled: candidateEnabled && scopeReady,
    selectedStoreId,
  });
  const operations = useDashboardV2Operations({
    user,
    selectedStoreId,
    enabled: scopeReady,
  });
  return {
    activeAnalytics,
    candidateAnalytics,
    operations,
    scopeReady,
    authorityError:
      activeAnalytics.authorityError ??
      candidateAnalytics.authorityError ??
      operations.authorityError,
  };
}

interface DashboardDetailHookBase {
  user?: CurrentUser | null;
  isOpen: boolean;
  entityId: string;
}

type DashboardLoadDetailHookInput = DashboardDetailHookBase & {
  kind: "load";
  period: null;
  sort: "created";
};

type DashboardHistoricalDetailHookInput = DashboardDetailHookBase & {
  kind: Exclude<DashboardDetailKind, "load">;
  period: DashboardPeriodState;
  sort: "recent";
};

export type DashboardV2DetailHookInput =
  | DashboardLoadDetailHookInput
  | DashboardHistoricalDetailHookInput;

export function useDashboardV2Detail(input: DashboardV2DetailHookInput) {
  const authorityState = useMemo(
    () => resolveDashboardAuthority(input.user),
    [input.user],
  );
  const detailInput = authorityState.authority
    ? ({
        ...authorityState.authority,
        kind: input.kind,
        entityId: input.entityId,
        period: input.period,
        sort: input.sort,
        cursor: undefined,
      } as DashboardDetailKeyInput)
    : null;
  const query = useInfiniteQuery({
    queryKey: detailInput
      ? dashboardKeys.detailOverview(detailInput)
      : [
          ...dashboardKeys.all,
          "detail",
          "disabled",
          input.user?.id ?? null,
          input.kind,
          input.entityId,
        ],
    queryFn: ({ signal, pageParam }) => {
      if (!detailInput) {
        throw new DashboardAuthorityContractError("authority missing");
      }
      return fetchDashboardV2Detail(
        {
          ...detailInput,
          cursor: typeof pageParam === "string" ? pageParam : undefined,
        } as DashboardDetailKeyInput,
        signal,
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_cursor : undefined,
    enabled: input.isOpen && detailInput !== null,
    staleTime: 60_000,
    retry: 1,
  });
  return { ...query, authorityError: authorityState.error };
}

export type DashboardV2RankingHookInput = {
  user?: CurrentUser | null;
  isOpen: boolean;
  period: DashboardPeriodState;
} & DashboardRankingContext;

export function useDashboardV2Ranking(input: DashboardV2RankingHookInput) {
  const authorityState = useMemo(
    () => resolveDashboardAuthority(input.user),
    [input.user],
  );
  const rankingInput = authorityState.authority
    ? ({
        ...authorityState.authority,
        period: input.period,
        kind: input.kind,
        sort: input.sort,
      } as DashboardRankingKeyInput)
    : null;
  const query = useInfiniteQuery({
    queryKey: rankingInput
      ? dashboardKeys.ranking(rankingInput)
      : [
          ...dashboardKeys.all,
          "ranking",
          "disabled",
          input.user?.id ?? null,
          input.kind,
          input.sort,
        ],
    queryFn: ({ signal, pageParam }) => {
      if (!rankingInput) {
        throw new DashboardAuthorityContractError("authority missing");
      }
      return fetchDashboardV2Ranking(
        {
          ...rankingInput,
          cursor: typeof pageParam === "string" ? pageParam : undefined,
        } as DashboardRankingFetchInput,
        signal,
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_cursor : undefined,
    enabled: input.isOpen && rankingInput !== null,
    staleTime: 60_000,
    retry: 1,
  });
  return { ...query, authorityError: authorityState.error };
}

export function useOrderOverview(date: string) {
  return useQuery({
    queryKey: ["orders", "overview", date],
    queryFn: () => fetchOrderOverview(date),
    staleTime: 60 * 1000,
  });
}
